/**
 * The People dashboard's metrics, computed in one place.
 *
 * A card's number and the list it opens are the same computation, so they cannot
 * disagree: the dashboard calls `peopleMetricCounts` and the Employees page
 * calls `peopleMetricIds` over identical inputs (employees + today's attendance,
 * approved leave and roster). Change the definition here and both move together.
 *
 * Every set is limited to the current team (non-exited employees), which is what
 * makes "not checked in" well defined: everyone we might expect in, minus those
 * already accounted for as present, on approved leave, on a weekly off or a
 * holiday.
 */
import type { Attendance, EmployeeDirectory, EmployeeFull, LeaveRequest, Shift } from "../api/types";

export type PeopleMetric = "team" | "present" | "on_leave" | "not_checked_in";

export const PEOPLE_METRICS: PeopleMetric[] = ["team", "present", "on_leave", "not_checked_in"];

export const PEOPLE_METRIC_LABEL: Record<PeopleMetric, string> = {
  team: "Team members",
  present: "Present today",
  on_leave: "On leave",
  not_checked_in: "Not checked in",
};

export const PEOPLE_METRIC_BLURB: Record<PeopleMetric, string> = {
  team: "Everyone currently employed.",
  present: "Checked in or marked present for the day.",
  on_leave: "On approved leave covering the selected date.",
  not_checked_in: "Expected today, without a check-in, leave, weekly off or holiday.",
};

export function isPeopleMetric(value: string | null | undefined): value is PeopleMetric {
  return (
    value === "team" ||
    value === "present" ||
    value === "on_leave" ||
    value === "not_checked_in"
  );
}

/** The trimmed shapes the metrics need, so either serializer's output fits. */
export interface MetricEmployee {
  id: string;
  status?: string | null;
}
export interface MetricAttendance {
  employee: string;
  status?: string | null;
}
export interface MetricLeave {
  employee?: string | null;
  status?: string | null;
  from_date: string;
  to_date: string;
}
export interface MetricShift {
  employee: string;
  shift_date: string;
  is_off?: boolean | null;
}

export interface MetricInputs {
  employees: MetricEmployee[];
  attendance: MetricAttendance[];
  leave: MetricLeave[];
  shifts: MetricShift[];
}

/** A present record; a half day still means they came in. */
const PRESENT_STATUSES = new Set(["present", "half_day"]);

export interface MetricSets {
  team: Set<string>;
  present: Set<string>;
  on_leave: Set<string>;
  not_checked_in: Set<string>;
  week_off: Set<string>;
  holiday: Set<string>;
}

/**
 * One pass over the same inputs, producing every set. `date` is the account's
 * selected work date as `YYYY-MM-DD`; leave covers it inclusively.
 */
export function peopleMetricSets(inputs: MetricInputs, date: string): MetricSets {
  const team = new Set<string>();
  for (const employee of inputs.employees) {
    if (employee.status === "exited") continue;
    team.add(employee.id);
  }

  const present = new Set<string>();
  const onLeave = new Set<string>();
  const weekOff = new Set<string>();
  const holiday = new Set<string>();

  for (const row of inputs.attendance) {
    if (!team.has(row.employee)) continue;
    const status = row.status ?? "present";
    if (PRESENT_STATUSES.has(status)) present.add(row.employee);
    else if (status === "on_leave") onLeave.add(row.employee);
    else if (status === "week_off") weekOff.add(row.employee);
    else if (status === "holiday") holiday.add(row.employee);
  }

  // Approved leave is authoritative and may cover days with no attendance row
  // yet, so it is unioned in rather than relied on to have written one.
  for (const request of inputs.leave) {
    if (!request.employee || request.status !== "approved") continue;
    if (!team.has(request.employee)) continue;
    if (request.from_date <= date && date <= request.to_date) onLeave.add(request.employee);
  }

  // An explicitly rostered OFF day is a weekly off even without an attendance row.
  for (const shift of inputs.shifts) {
    if (shift.is_off && team.has(shift.employee)) weekOff.add(shift.employee);
  }

  const notCheckedIn = new Set<string>();
  for (const id of team) {
    if (present.has(id) || onLeave.has(id) || weekOff.has(id) || holiday.has(id)) continue;
    notCheckedIn.add(id);
  }

  return { team, present, on_leave: onLeave, not_checked_in: notCheckedIn, week_off: weekOff, holiday };
}

/** The employee ids behind one metric, for a filtered list. */
export function peopleMetricIds(metric: PeopleMetric, inputs: MetricInputs, date: string): Set<string> {
  return peopleMetricSets(inputs, date)[metric];
}

/** The number behind one card. The same inputs, so it matches the list. */
export function peopleMetricCounts(inputs: MetricInputs, date: string): Record<PeopleMetric, number> {
  const sets = peopleMetricSets(inputs, date);
  return {
    team: sets.team.size,
    present: sets.present.size,
    on_leave: sets.on_leave.size,
    not_checked_in: sets.not_checked_in.size,
  };
}

/** Convenience: the trimmed inputs from the shapes the pages already fetch. */
export function metricInputsFrom(parts: {
  employees: EmployeeFull[] | EmployeeDirectory[];
  attendance: Attendance[];
  leave: LeaveRequest[];
  shifts: Shift[];
}): MetricInputs {
  return {
    employees: parts.employees.map((e) => ({ id: e.id, status: e.status })),
    attendance: parts.attendance.map((a) => ({ employee: a.employee, status: a.status })),
    leave: parts.leave.map((l) => ({
      employee: l.employee,
      status: l.status,
      from_date: l.from_date,
      to_date: l.to_date,
    })),
    shifts: parts.shifts.map((s) => ({
      employee: s.employee,
      shift_date: s.shift_date,
      is_off: s.is_off,
    })),
  };
}
